import Alert from "react-bootstrap/Alert";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { ChangeEvent } from "react";
import { AvailablePropertyTypes } from "@enums/available-property-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { RadioInputFormGroup } from "../radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../input-form-group/input-form-group";
import { TextareaFormGroup } from "../textarea-form-group/textarea-form-group";
import { SelectFormGroup } from "../select-form-group/select-form-group";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";

type RpServiceConfigurationPropertyFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    automationId?: string;
    ariaLabel?: string;
    placeholder?: string;
    label: string;
    value: string;
    selectValue: SelectOption;
    type?: string;
    onChangeRadio?: (value: string) => void;
    onChangeText?: (event: ChangeEvent<HTMLInputElement>) => void;
    onChangeSelect?: (event: SelectOption) => void;
    options?: Array<SelectOption>;
    layout?: LayoutTypes;
    disabled: boolean;
    isRequired: boolean;
    autoComplete?: "true" | "off";
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty;
};

export const RpServiceConfigurationPropertyFormGroup = (props: RpServiceConfigurationPropertyFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        automationId,
        ariaLabel,
        placeholder,
        label,
        value,
        selectValue,
        onChangeRadio,
        onChangeText,
        onChangeSelect,
        options,
        layout,
        disabled,
        isRequired,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        rpServiceConfigurationProperty
    } = props;

    switch (rpServiceConfigurationProperty.type) {
        case AvailablePropertyTypes.BOOLEAN:
            if (onChangeRadio && options) {
                return (
                    <RadioInputFormGroup
                        controlId={controlId}
                        automationId={automationId}
                        isRequired={isRequired}
                        label={label}
                        onChange={onChangeRadio}
                        options={options}
                        value={selectValue?.value || ""}
                        disabled={disabled}
                        labelWidth={labelWidth}
                        layout={layout}
                        afterInputIcon={afterInputIcon}
                        afterInputIconDisabled={afterInputIconDisabled}
                        afterInputIconOnClick={afterInputIconOnClick}
                    />
                );
            } else {
                return <Alert variant={"danger"}>onChangeRadio function was not provided</Alert>;
            }
        case AvailablePropertyTypes.INTEGER:
        case AvailablePropertyTypes.TEXT:
        case AvailablePropertyTypes.STRING:
            if (onChangeText) {
                return (
                    <InputFormGroup
                        controlId={controlId}
                        automationId={automationId}
                        label={label}
                        ariaLabel={ariaLabel}
                        onChange={onChangeText}
                        value={value}
                        disabled={disabled}
                        isRequired={isRequired}
                        layout={layout}
                        labelWidth={labelWidth}
                        placeholder={placeholder}
                        afterInputIcon={afterInputIcon}
                        afterInputIconDisabled={afterInputIconDisabled}
                        afterInputIconOnClick={afterInputIconOnClick}
                    />
                );
            } else {
                return <Alert variant={"danger"}>onChangeText function was not provided</Alert>;
            }
        case AvailablePropertyTypes.JSON:
        case AvailablePropertyTypes.TEXTAREA:
            if (onChangeText) {
                return (
                    <TextareaFormGroup
                        controlId={controlId}
                        automationId={automationId}
                        label={label}
                        ariaLabel={ariaLabel}
                        onChange={onChangeText}
                        value={value}
                        disabled={disabled}
                        isRequired={isRequired}
                        layout={layout}
                        labelWidth={labelWidth}
                        placeholder={placeholder}
                        afterInputIcon={afterInputIcon}
                        afterInputIconDisabled={afterInputIconDisabled}
                        afterInputIconOnClick={afterInputIconOnClick}
                    />
                );
            } else {
                return <Alert variant={"danger"}>onChangeText function was not provided</Alert>;
            }
        case AvailablePropertyTypes.SELECT:
            if (onChangeSelect) {
                return (
                    <SelectFormGroup
                        controlId={controlId}
                        automationId={automationId}
                        isRequired={isRequired}
                        label={label}
                        ariaLabel={ariaLabel}
                        onChange={onChangeSelect}
                        options={options}
                        selectedOption={selectValue}
                        disabled={disabled}
                        labelWidth={labelWidth}
                        layout={layout}
                        isClearable={false}
                        isSearchable={true}
                        afterInputIcon={afterInputIcon}
                        afterInputIconDisabled={afterInputIconDisabled}
                        afterInputIconOnClick={afterInputIconOnClick}
                    />
                );
            } else {
                return <Alert variant={"danger"}>onChangeSelect function was not provided</Alert>
            }
        default:
            return <Alert variant={"danger"}>The property type &quot;{rpServiceConfigurationProperty.type}&quot; does not match any existing UI input</Alert>;
    }
};